import { useState, useEffect, useCallback } from 'react';
import { api } from '../../api';

const STATUS_STYLES = {
  current:       { bg: '#dcfce7', color: '#15803d', label: 'Current' },
  expiring_soon: { bg: '#fef3c7', color: '#b45309', label: 'Expiring Soon' },
  expired:       { bg: '#fee2e2', color: '#dc2626', label: 'Expired' },
  pending:       { bg: '#e0e7ff', color: '#4338ca', label: 'Pending' },
  not_applicable:{ bg: '#f1f5f9', color: '#64748b', label: 'N/A' },
};

const EMPTY_FORM = {
  category_id: '', title: '', description: '', expiration_date: '',
  is_required: 1, reminder_days: '30,7,1', notes: '',
};

export default function EntityCompliancePanel({ entityType, entityId, entityName }) {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [expandedItem, setExpandedItem] = useState(null);
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplateSelect, setShowTemplateSelect] = useState(false);
  const [applying, setApplying] = useState(false);

  const loadItems = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const data = await api.getComplianceItems({ entity_type: entityType, entity_id: entityId });
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    }
    setLoading(false);
  }, [entityType, entityId]);

  const loadCategories = useCallback(async () => {
    try {
      const data = await api.getComplianceCategories();
      setCategories(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      const data = await api.getComplianceTemplates({ entity_type: entityType });
      setTemplates(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
  }, [entityType]);

  useEffect(() => { loadItems(); loadCategories(); loadTemplates(); }, [loadItems, loadCategories, loadTemplates]);

  const loadDocs = useCallback(async (itemId) => {
    try {
      const data = await api.getComplianceDocuments(itemId);
      setDocs(Array.isArray(data) ? data : []);
    } catch { setDocs([]); }
  }, []);

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? (checked ? 1 : 0) : value }));
  }

  function openAdd() {
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setShowForm(true);
    setError('');
  }

  function openEdit(item) {
    setEditing(item);
    setForm({
      category_id: item.category_id || '',
      title: item.title || '',
      description: item.description || '',
      expiration_date: item.expiration_date || '',
      is_required: item.is_required ?? 1,
      reminder_days: item.reminder_days || '30,7,1',
      notes: item.notes || '',
    });
    setShowForm(true);
    setError('');
  }

  async function handleSave() {
    if (!form.title.trim() || !form.category_id) {
      setError('Title and category are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = { ...form, entity_type: entityType, entity_id: entityId };
      if (editing) {
        await api.updateComplianceItem(editing.id, payload);
      } else {
        await api.createComplianceItem(payload);
      }
      setShowForm(false);
      loadItems();
    } catch (err) {
      setError(err.message || 'Failed to save');
    }
    setSaving(false);
  }

  async function handleDelete(id) {
    if (!window.confirm('Deactivate this compliance item?')) return;
    try {
      await api.deleteComplianceItem(id);
      loadItems();
    } catch { /* silent */ }
  }

  function toggleExpand(itemId) {
    if (expandedItem === itemId) {
      setExpandedItem(null);
      setDocs([]);
    } else {
      setExpandedItem(itemId);
      loadDocs(itemId);
    }
  }

  async function handleUpload(e, itemId) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.uploadComplianceDocument(itemId, fd);
      loadDocs(itemId);
    } catch { /* silent */ }
    setUploading(false);
    e.target.value = '';
  }

  async function handleDeleteDoc(docId) {
    try {
      await api.deleteComplianceDocument(docId);
      loadDocs(expandedItem);
    } catch { /* silent */ }
  }

  function getStatus(item) {
    return item.computed_status || item.manual_status || 'not_applicable';
  }

  function daysInfo(item) {
    if (!item.expiration_date) return null;
    const exp = new Date(item.expiration_date + 'T00:00:00');
    const now = new Date();
    now.setHours(0,0,0,0);
    const diff = Math.ceil((exp - now) / 86400000);
    if (diff < 0) return { text: `${Math.abs(diff)}d overdue`, color: '#dc2626' };
    if (diff === 0) return { text: 'Expires today', color: '#d97706' };
    if (diff <= 30) return { text: `${diff}d remaining`, color: '#b45309' };
    return { text: `${diff}d remaining`, color: '#15803d' };
  }

  async function applyTemplate(templateId) {
    setApplying(true);
    setError('');
    try {
      const result = await api.applyComplianceTemplate(templateId, { entity_type: entityType, entity_id: entityId });
      setShowTemplateSelect(false);
      loadItems();
      alert(result.message || 'Template applied');
    } catch (err) {
      setError(err.message || 'Failed to apply template');
    }
    setApplying(false);
  }

  // Summary counts
  const expired = items.filter(i => getStatus(i) === 'expired').length;
  const expiring = items.filter(i => getStatus(i) === 'expiring_soon').length;
  const current = items.filter(i => getStatus(i) === 'current').length;

  return (
    <div className="entity-compliance-panel">
      <div className="ecp-header">
        <div>
          <h3 className="ecp-title">Compliance Tracking</h3>
          {items.length > 0 && (
            <div className="ecp-summary">
              <span className="ecp-count current">{current} current</span>
              {expiring > 0 && <span className="ecp-count expiring">{expiring} expiring</span>}
              {expired > 0 && <span className="ecp-count expired">{expired} expired</span>}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          {templates.length > 0 && (
            <button className="btn btn-sm btn-outline" onClick={() => setShowTemplateSelect(!showTemplateSelect)}>
              Apply Template
            </button>
          )}
          <button className="btn btn-sm btn-primary" onClick={openAdd}>+ Add Item</button>
        </div>
      </div>

      {/* Template selector */}
      {showTemplateSelect && (
        <div style={{ padding: '12px 18px', background: '#eff6ff', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#1e40af', marginBottom: '8px' }}>
            Select a template to apply:
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {templates.map(t => (
              <button
                key={t.id}
                className="btn btn-sm btn-outline"
                onClick={() => applyTemplate(t.id)}
                disabled={applying}
                style={{ fontSize: '12px' }}
              >
                {t.name} ({t.item_count} items)
              </button>
            ))}
            <button className="btn btn-sm" onClick={() => setShowTemplateSelect(false)} style={{ fontSize: '12px', color: '#64748b' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Add/Edit form */}
      {showForm && (
        <div className="ecp-form">
          <div className="ecp-form-grid">
            <div className="form-group">
              <label>Category *</label>
              <select name="category_id" value={form.category_id} onChange={handleChange}>
                <option value="">Select...</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Title *</label>
              <input name="title" value={form.title} onChange={handleChange} placeholder="e.g., CDL Class A" />
            </div>
            <div className="form-group">
              <label>Expiration Date</label>
              <input type="date" name="expiration_date" value={form.expiration_date} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Reminder Days</label>
              <input name="reminder_days" value={form.reminder_days} onChange={handleChange} placeholder="30,7,1" />
            </div>
          </div>
          <div className="form-group" style={{ marginTop: 8 }}>
            <label>Description</label>
            <input name="description" value={form.description} onChange={handleChange} placeholder="Optional details" />
          </div>
          <div className="form-group" style={{ marginTop: 8 }}>
            <label>Notes</label>
            <textarea name="notes" value={form.notes} onChange={handleChange} rows="2" placeholder="Internal notes" />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem' }}>
              <input type="checkbox" name="is_required" checked={form.is_required === 1} onChange={handleChange} />
              Required
            </label>
          </div>
          {error && <div className="form-error" style={{ marginTop: 8 }}>{error}</div>}
          <div className="ecp-form-actions">
            <button className="btn btn-sm" onClick={() => setShowForm(false)}>Cancel</button>
            <button className="btn btn-sm btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : editing ? 'Update' : 'Add'}
            </button>
          </div>
        </div>
      )}

      {/* Items list */}
      {loading ? (
        <div className="ecp-empty">Loading...</div>
      ) : items.length === 0 ? (
        <div className="ecp-empty">No compliance items tracked for this {entityType}.</div>
      ) : (
        <div className="ecp-items">
          {items.map(item => {
            const status = getStatus(item);
            const style = STATUS_STYLES[status] || STATUS_STYLES.not_applicable;
            const days = daysInfo(item);
            const isExpanded = expandedItem === item.id;

            return (
              <div key={item.id} className={`ecp-item ${isExpanded ? 'expanded' : ''}`}>
                <div className="ecp-item-row" onClick={() => toggleExpand(item.id)}>
                  <div className="ecp-item-main">
                    <span className="ecp-item-title">{item.title}</span>
                    {item.category_name && <span className="ecp-item-cat">{item.category_name}</span>}
                  </div>
                  <div className="ecp-item-right">
                    {days && <span style={{ fontSize: '0.75rem', color: days.color, fontWeight: 600 }}>{days.text}</span>}
                    <span className="ecp-status-badge" style={{ background: style.bg, color: style.color }}>
                      {style.label}
                    </span>
                    {item.is_required ? <span className="ecp-required">REQ</span> : null}
                  </div>
                </div>

                {isExpanded && (
                  <div className="ecp-item-detail">
                    <div className="ecp-detail-grid">
                      {item.expiration_date && <div><strong>Expires:</strong> {item.expiration_date}</div>}
                      {item.description && <div><strong>Description:</strong> {item.description}</div>}
                      {item.notes && <div><strong>Notes:</strong> {item.notes}</div>}
                      {item.reminder_days && <div><strong>Reminders:</strong> {item.reminder_days} days before</div>}
                    </div>

                    {/* Documents */}
                    <div className="ecp-docs">
                      <div className="ecp-docs-header">
                        <strong>Documents</strong>
                        <label className="btn btn-xs btn-outline" style={{ cursor: 'pointer' }}>
                          {uploading ? 'Uploading...' : 'Upload'}
                          <input type="file" hidden onChange={(e) => handleUpload(e, item.id)} />
                        </label>
                      </div>
                      {docs.length === 0 && <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>No documents</div>}
                      {docs.map(doc => (
                        <div key={doc.id} className="ecp-doc-row">
                          <a href={`/api/uploads/${doc.file_path}`} target="_blank" rel="noreferrer" className="ecp-doc-name">
                            {doc.original_name}
                          </a>
                          {doc.is_current ? <span className="ecp-doc-current">Current</span> : null}
                          <button className="btn-icon-sm" onClick={() => handleDeleteDoc(doc.id)} title="Delete">&times;</button>
                        </div>
                      ))}
                    </div>

                    <div className="ecp-item-actions">
                      <button className="btn btn-xs" onClick={() => openEdit(item)}>Edit</button>
                      <button className="btn btn-xs btn-danger" onClick={() => handleDelete(item.id)}>Deactivate</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
